// ──────────────────────────────────────────────
// 顧客サイトに置くタグの先導役（embed.js）。
//
// 【なぜ分けたか】
// 以前は1本で、描画の一式（約76KB・圧縮後）を全部読み終わってから、やっと
// 「どのLPを出すか・その中身は何か」を聞きに行っていた。順番に待つので、
// 本番の実測では タグの到着 1.4秒 → 問い合わせ 1.6秒 → 描画 だった。
// ここを、2KBの先導役と描画本体に割る。先導役は
//   ・中身の問い合わせ
//   ・描画本体の読み込み
// を同時に始める。どちらも終わった時点で描画するので、重なったぶん早く出る。
//
// 【壊れない形にしてある】
// ・先導役が中身を取れなくても、描画本体が自分でもう一度頼む（単独でも動く）
// ・描画本体が読めなければ何も出ない。1本だったときも同じで、増えた危険はない
// ・古い1本のタグが顧客のブラウザに残っていても、そのまま動く（口は両方受ける）
// ──────────────────────────────────────────────
import { prefetch } from "./doc-fetch";

/** このタグ自身のURL。描画本体の場所も、問い合わせ先も、ここから決める。 */
function selfSrc(): string {
  const cur = document.currentScript as HTMLScriptElement | null;
  if (cur?.src) return cur.src;
  // async で currentScript が取れない場合（古いブラウザ等）は、自分を名前で探す。
  const all = document.querySelectorAll<HTMLScriptElement>('script[src*="embed.js"]');
  return all.length ? all[all.length - 1].src : "";
}

const SELF = selfSrc();
const API_BASE = (() => {
  try { return SELF ? new URL(SELF).origin : window.location.origin; } catch { return window.location.origin; }
})();

/** 描画本体のURL。先導役と同じ場所に置いてある。?v= のような指定はそのまま引き継ぐ。 */
function coreSrc(): string {
  try {
    const u = new URL(SELF || `${API_BASE}/embed.js`);
    u.pathname = u.pathname.replace(/embed\.js$/, "embed-core.js");
    return u.toString();
  } catch {
    return `${API_BASE}/embed-core.js`;
  }
}

// 描画本体に、自分が読み込まれた場所を伝える。描画本体は動的に差し込まれるので、
// 自分の src から配信元を決める作りのままだと、場所の指定が効かない場合がある。
(window as unknown as { __clEmbedBase?: string }).__clEmbedBase = API_BASE;

/** いまページにある埋め込み先を見て、中身を先に頼む。
 *  1ページに何個あっても構わないが、頼む数には上限を置く（事故で100個あっても巻き込まれない）。 */
const MAX_PREFETCH = 3;
function askForWhatIsOnThePage(): number {
  let n = 0;
  const take = (kind: "p" | "l", v: string | null | undefined, base?: string | null): void => {
    if (!v || n >= MAX_PREFETCH) return;
    prefetch(base || API_BASE, kind, v);
    n++;
  };
  try {
    document.querySelectorAll<HTMLElement>("chipper-lp").forEach((el) => {
      const api = el.getAttribute("data-api");
      const pid = el.getAttribute("data-project");
      if (pid) take("p", pid, api);
      else take("l", el.getAttribute("data-id"), api);
    });
    // タグを消してしまう置き場（WordPress 等）向けの書き方も同じように見る。
    document.querySelectorAll<HTMLElement>('[id^="cl-lp-"], [id^="cl-lpp-"]').forEach((el) => {
      const t = el.tagName;
      if (t === "CHIPPER-LP" || t === "IFRAME" || t === "SCRIPT") return;
      const api = el.getAttribute("data-api");
      if (el.id.indexOf("cl-lpp-") === 0) take("p", el.id.slice("cl-lpp-".length), api);
      else take("l", el.id.slice("cl-lp-".length), api);
    });
  } catch {
    /* 見つからなければ描画本体が自分で頼む */
  }
  return n;
}

/** 描画本体を読み込む。 */
function loadCore(): void {
  const w = window as unknown as { __clCoreLoading?: number };
  if (w.__clCoreLoading) return;
  w.__clCoreLoading = 1;
  const sc = document.createElement("script");
  sc.src = coreSrc();
  sc.async = true;
  (document.head || document.documentElement).appendChild(sc);
}

const found = askForWhatIsOnThePage();
loadCore();

// 埋め込み先がまだ読まれていなかった場合（タグが先に動いた）、読み終わりでもう一度見る。
// このときはもう描画本体を読み込んでいるので、重なるのはここからの問い合わせだけ。
if (!found && document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => { askForWhatIsOnThePage(); }, { once: true });
}
